/**
 * useWindowResize Hook
 *
 * Detects when the browser window resize has stopped and triggers a callback.
 * Uses a debounced approach - polls every second during resize, triggers
 * callback when resize has stopped for the specified delay.
 */
import { useState, useEffect, useCallback } from 'react';

interface UseWindowResizeOptions {
  /** Delay in ms after resize stops before triggering (default: 150ms) */
  debounceDelay?: number;
}

interface WindowSize {
  width: number;
  height: number;
}

/**
 * Hook that returns current window size and a resize counter that increments
 * when window resize has stopped. Use the counter as a key to force re-renders.
 */
export function useWindowResize(options: UseWindowResizeOptions = {}) {
  const { debounceDelay = 150 } = options;

  const [windowSize, setWindowSize] = useState<WindowSize>({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });

  // Counter that increments when resize stops - use as key to force re-render
  const [resizeCount, setResizeCount] = useState(0);

  // Track if currently resizing
  const [isResizing, setIsResizing] = useState(false);

  const handleResizeEnd = useCallback(() => {
    setWindowSize({
      width: window.innerWidth,
      height: window.innerHeight,
    });
    setResizeCount((c) => c + 1);
    setIsResizing(false);
  }, []);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const handleResize = () => {
      setIsResizing(true);

      // Clear existing timeout
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      // Set new timeout - will fire when resize stops
      timeoutId = setTimeout(handleResizeEnd, debounceDelay);
    };

    // Add resize listener
    window.addEventListener('resize', handleResize);

    // Initial size
    setWindowSize({
      width: window.innerWidth,
      height: window.innerHeight,
    });

    return () => {
      window.removeEventListener('resize', handleResize);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [debounceDelay, handleResizeEnd]);

  return {
    windowSize,
    resizeCount,
    isResizing,
  };
}
